/**
 * The owed second channel (ruling, Isaiah Cotto 2026-10-13: "First moves, other tracked").
 *
 * The business requires BOTH a fax to DCF and an upload to the resident's DCF account (Kaleigh Vazquez & Josie Evans,
 * "PA Sale to Approval - IF THIS THEN THAT", 2026-10-08, SUB 05). The FIRST recorded sending moves the case to UMED
 * Submitted to DCF, as before. The OTHER step is then owed on the case until Gwynn records it for the SAME packet
 * revision -- it never moves the case, never notifies Josie again and never opens a second ten-day check.
 *
 * This says, in plain words, which step was done and which is owed ("Uploaded to DCF 10/12 ✓ · Faxed to DCF: owed"),
 * and offers the owed act for that exact revision: "Fax to DCF (owed)" (through the fax service) and "Record fax sending
 * (owed)" after an upload, or "Confirm DCF upload (owed)" after a fax. Every state is in words with a text marker,
 * never by colour alone; a held act reads as not ready and a click says what holds it.
 */
import React, { useRef, useState } from 'react';
import { umedApi, shortDate, freshKey } from './umedApi';
import type { PacketPreparationStatus } from './umedApi';

export type SecondChannelMethod = 'FAX' | 'PORTAL_UPLOAD';
/** GET …/packet-preparation `second_channel` (services/umed/nativeWorkflowService.secondChannelStatus). */
export interface SecondChannelStatus {
  owed: SecondChannelMethod | null;
  channel: SecondChannelMethod;
  /** BLOCKED (2026-10-14): still open on the server, but its revision carries a document marked "Not this resident's". */
  state: 'OWED' | 'RECORDED' | 'SUPERSEDED' | 'BLOCKED';
  /** 2026-10-14: closed because the revision carries a document marked "Not this resident's" (never sent again). */
  closed_for?: 'WRONG_DOCUMENT_SENT' | string | null;
  action_id: string;
  first: { method: string; channel: string | null; on: string | null; submission_id: string };
  packet: { packet_id: string; version: number | null; sha256: string | null; date_submitted: string | null; episode_key: string | null; carries_misfiled?: string[] };
  second: { method: string; on: string | null; recorded_at: string | null; via: string | null; verified: boolean; transmission_id: string | null } | null;
  /** An owed upload can be confirmed now (not while the DCF benefits link is stuck, nor before the DCF account is made). */
  upload_ready?: boolean;
  /** What holds the owed upload (2026-10-13): the stuck benefits link, or the DCF account not yet made. */
  upload_held_by?: 'BENEFITS_STUCK' | 'DCF_ACCOUNT' | null;
}
export type WithSecondChannel = PacketPreparationStatus & { second_channel?: SecondChannelStatus | null };

type Outcome = { success: boolean; error?: string; message?: string; code?: string; status?: number; data?: any };
const said = (r: Outcome): string => {
  if (r.status === undefined) return 'Could not reach the server. Nothing was recorded.';
  if (r.status >= 500) return 'The server failed. Nothing was recorded, and it is not a permissions problem.';
  return r.error || r.message || (r.code ? `Refused (${r.code}).` : 'The server refused this.');
};
const floridaToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const usDay = (iso: string) => (iso ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : '');

const DONE_WORDS: Record<SecondChannelMethod, string> = { FAX: 'Faxed to DCF', PORTAL_UPLOAD: 'Uploaded to DCF' };
const firstMethodOf = (sc: SecondChannelStatus): SecondChannelMethod => (sc.channel === 'FAX' ? 'PORTAL_UPLOAD' : 'FAX');

/** The owed line in plain words: what was done (✓, with its date) and what is owed. */
export function secondChannelLine(sc: SecondChannelStatus): string {
  const first = firstMethodOf(sc);
  const done = `${DONE_WORDS[first]}${sc.first.on ? ` ${shortDate(sc.first.on)}` : ''} ✓`;
  if (sc.state === 'OWED') return `${done} · ${DONE_WORDS[sc.channel]}: owed`;
  if ((sc.state === 'SUPERSEDED' || sc.state === 'BLOCKED') && sc.closed_for === 'WRONG_DOCUMENT_SENT') {
    return `${done} · ${DONE_WORDS[sc.channel]}: not to be done (revision ${sc.packet.version ?? '?'} carries a document marked "Not this resident's"; it is never sent again)`;
  }
  if (sc.state === 'SUPERSEDED') return `${done} · ${DONE_WORDS[sc.channel]}: no longer owed (a newer sending replaced revision ${sc.packet.version ?? '?'})`;
  const on = sc.second && sc.second.on ? ` ${shortDate(sc.second.on)}` : '';
  return `${done} · ${DONE_WORDS[sc.channel]}${on} ✓`;
}

/** The row marker's words (BoardTable): "Fax owed" / "Upload owed"; null when nothing is owed. */
export function secondChannelMarker(owed: string | null | undefined): string | null {
  if (owed === 'FAX') return 'Fax owed';
  if (owed === 'PORTAL_UPLOAD') return 'Upload owed';
  return null;
}

export default function PacketSecondChannel({ caseId, status, canSubmit, onDone }: {
  caseId: string; status: WithSecondChannel; canSubmit: boolean; onDone: (msg: string) => void | Promise<void>;
}) {
  const sc = status.second_channel || null;
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<'fax' | 'sendfax' | 'upload' | null>(null);
  const [holds, setHolds] = useState<string | null>(null);
  const [attest, setAttest] = useState(false);
  const [day, setDay] = useState(floridaToday());
  const [dayAttest, setDayAttest] = useState(false);
  const [reference, setReference] = useState('');
  const key = useRef<string>(freshKey());
  if (!sc) return null;

  const pk = sc.packet;
  // 2026-10-14 (PR #416 round 2): a revision carrying a document marked "Not this resident's" is never offered for sending.
  const carriesMisfiled = Boolean(pk.carries_misfiled && pk.carries_misfiled.length);
  const owed = carriesMisfiled ? null : sc.owed;
  const coverDay = pk.date_submitted || '';
  const ft = status.fax_transmission || null;
  const faxBusy = Boolean(ft && (ft.in_flight || (ft.status === 'DELIVERED' && ft.sending === 'PENDING')));
  const serviceOn = Boolean(status.fax_service?.enabled);
  const canSendFax = status.fax_service?.can_send === true;
  const otherDay = Boolean(coverDay && day && day !== coverDay);
  const today = floridaToday();
  const sendOtherDay = Boolean(coverDay && coverDay !== today);
  const verb = open === 'upload' ? 'uploaded' : 'faxed';
  const dayLine = otherDay ? `I ${verb} the revision dated ${usDay(coverDay)} on ${usDay(day)}` : null;
  const sendLine = sendOtherDay ? `Fax the revision dated ${usDay(coverDay)} on ${usDay(today)}` : null;

  const begin = (which: 'fax' | 'sendfax' | 'upload') => {
    setErr(null);
    let hold: string | null = null;
    if (which !== 'upload' && faxBusy) hold = 'A fax eMerge sent to DCF is still in progress or being recorded: wait for its result (see Fax to DCF below).';
    else if (which === 'sendfax' && !canSendFax) hold = 'A fax of this UMED is already with the fax service or waiting for a person: see Fax to DCF below.';
    else if (which === 'upload' && sc.upload_ready === false) {
      // 2026-10-13: a fax may go first on an account that could not be created; the upload still needs it made.
      hold = sc.upload_held_by === 'DCF_ACCOUNT'
        ? (status.dcf_account?.upload_hold || 'The DCF account is not recorded as created: the upload cannot be confirmed until it is (“DCF email created…” in the DCF account section).')
        : 'The DCF benefits are marked stuck: the upload cannot be confirmed until they are linked and the mark is cleared.';
    }
    if (hold) { setOpen(null); setHolds(hold); return; }
    setHolds(null); setAttest(false); setDayAttest(false); setDay(today); setReference(''); key.current = freshKey(); setOpen(which);
  };
  const run = async (name: string, fn: () => Promise<Outcome>, msg: (d: any) => string) => {
    setBusy(name); setErr(null);
    try {
      const r = await fn();
      if (!r.success) { setErr(said(r)); return; }
      setOpen(null); key.current = freshKey();
      await onDone(msg(r.data));
    } finally { setBusy(null); }
  };
  const done = (what: string) => (d: any) => (d && d.replayed ? `This ${what} was already recorded; nothing new was written.` : `Recorded — the owed ${what} is on the case. The case did not move.`);
  const recordFax = () => run('fax', () => umedApi.recordPacketFax(caseId, {
    packet_id: pk.packet_id, sha256: pk.sha256 || '', attested: true, faxed_on: day, ...(otherDay ? { date_attested: dayAttest } : {}),
    ...(reference.trim() ? { reference: reference.trim() } : {}), idempotency_key: key.current,
  }), done('fax'));
  const confirmUpload = () => run('upload', () => umedApi.confirmPacketUpload(caseId, {
    packet_id: pk.packet_id, sha256: pk.sha256 || '', attested: true, uploaded_on: day, ...(otherDay ? { date_attested: dayAttest } : {}),
    ...(reference.trim() ? { reference: reference.trim() } : {}), idempotency_key: key.current,
  }), done('upload'));
  const sendFax = () => run('sendfax', () => umedApi.faxPacketToDcf(caseId, {
    packet_id: pk.packet_id, sha256: pk.sha256 || '', confirmed: true, ...(sendOtherDay ? { date_attested: dayAttest } : {}), idempotency_key: key.current,
  }), (d) => (d && (d.status === 'DELIVERED') ? 'Fax delivered to DCF: the owed fax is recorded.' : 'Fax queued for DCF. The owed fax counts as done only when it is delivered.'));

  const notReady = (held: boolean) => (held ? ' um-notready' : '');
  return (
    <div className="um-second-channel" aria-label="Fax and upload" data-testid="second-channel">
      <p role="status">
        <strong>{secondChannelLine(sc)}</strong>
        {owed && <span className="um-quiet"> · revision {pk.version ?? '?'}{coverDay ? `, dated ${shortDate(coverDay)}` : ''}. DCF needs both; recording it does not move the case.</span>}
      </p>
      {carriesMisfiled && sc.owed && (
        <p className="um-notice stop" role="alert">Revision {pk.version ?? '?'} carries a document marked “Not this resident's”: do not fax or upload it. Add the right document and open a corrected re-send on the case.</p>
      )}
      {owed && canSubmit && (
        <div className="btns">
          {owed === 'FAX' && serviceOn && (
            <button type="button" className={`um-open um-primary${notReady(faxBusy || !canSendFax)}`} disabled={busy !== null}
              aria-disabled={faxBusy || !canSendFax ? true : undefined} aria-expanded={open === 'sendfax'} onClick={() => begin('sendfax')}>Fax to DCF (owed)</button>
          )}
          {owed === 'FAX' && (
            <button type="button" className={`um-open${notReady(faxBusy)}`} disabled={busy !== null} aria-disabled={faxBusy ? true : undefined}
              aria-expanded={open === 'fax'} onClick={() => begin('fax')}>Record fax sending (owed)</button>
          )}
          {owed === 'PORTAL_UPLOAD' && (
            <button type="button" className={`um-open um-primary${notReady(sc.upload_ready === false)}`} disabled={busy !== null}
              aria-disabled={sc.upload_ready === false ? true : undefined} aria-expanded={open === 'upload'} onClick={() => begin('upload')}>Confirm DCF upload (owed)</button>
          )}
        </div>
      )}
      {holds && <p className="um-notice um-holds" role="status" data-testid="second-channel-holds">{holds}</p>}
      {open === 'sendfax' && (
        <div className="um-form" role="group" aria-label="Fax the owed packet to DCF">
          <label className="um-check"><input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} aria-label="Send the owed fax" />
            <span>Send revision {pk.version ?? '?'} (fingerprint {(pk.sha256 || '').slice(0, 12)}…) to DCF by fax at {status.fax_service?.destination_display} — the same revision already uploaded.</span></label>
          {sendLine && (
            <label className="um-check"><input type="checkbox" checked={dayAttest} onChange={(e) => setDayAttest(e.target.checked)} aria-label="Fax date differs from the cover" />
              <span>{sendLine}.</span></label>
          )}
          <div className="btns">
            <button type="button" className="um-open um-primary" disabled={busy !== null || !attest || (sendOtherDay && !dayAttest)} onClick={sendFax}>{busy === 'sendfax' ? 'Sending…' : 'Send fax'}</button>
            <button type="button" className="um-open" disabled={busy !== null} onClick={() => setOpen(null)}>Cancel</button>
          </div>
          <p className="um-quiet">Queued is not delivered: the owed fax is recorded only when the fax service reports it delivered.</p>
        </div>
      )}
      {(open === 'fax' || open === 'upload') && (
        <div className="um-form" role="group" aria-label={open === 'fax' ? 'Record the owed fax' : 'Confirm the owed upload'}>
          <label className="um-check"><input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} aria-label={open === 'fax' ? 'I faxed this packet' : 'I uploaded this packet'} />
            <span>I {verb} revision {pk.version ?? '?'} of this packet (fingerprint {(pk.sha256 || '').slice(0, 12)}…) {open === 'fax' ? 'to DCF' : 'to the resident’s DCF account'}.</span></label>
          <label><span>{open === 'fax' ? 'Faxed on' : 'Uploaded on'}</span><input type="date" value={day} max={today} onChange={(e) => { setDay(e.target.value); setDayAttest(false); }} aria-label={open === 'fax' ? 'Faxed on' : 'Uploaded on'} /></label>
          {dayLine && (
            <label className="um-check"><input type="checkbox" checked={dayAttest} onChange={(e) => setDayAttest(e.target.checked)} aria-label="Date differs from the cover" />
              <span>{dayLine}.</span></label>
          )}
          <label><span>{open === 'fax' ? 'Fax confirmation or number (optional)' : 'DCF confirmation or reference (if shown)'}</span><input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={200} aria-label="Reference" /></label>
          <div className="btns">
            <button type="button" className="um-open um-primary" disabled={busy !== null || !attest || !day || (otherDay && !dayAttest)} onClick={open === 'fax' ? recordFax : confirmUpload}>
              {busy ? 'Recording…' : open === 'fax' ? 'Record fax' : 'Confirm upload'}
            </button>
            <button type="button" className="um-open" disabled={busy !== null} onClick={() => setOpen(null)}>Cancel</button>
          </div>
          <p className="um-quiet">Recorded as your confirmation, not as a receipt from DCF. The case stays where it is; Josie is not notified again.</p>
        </div>
      )}
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </div>
  );
}
